import React from 'react';
import { Box, Card, CardContent, Typography, Button, Alert, Chip } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { overallReport, AI_BASE_URL } from '../../services/aiBackend';
import api from '../../services/api';

const OverallReportPage: React.FC = () => {
  const [xerFile, setXerFile] = React.useState<File | null>(null);
  const [images, setImages] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [response, setResponse] = React.useState<any>(null);

  const handleXerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setXerFile(e.target.files[0]);
  };

  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      setImages([]);
      return;
    }
    setImages(Array.from(e.target.files));
  };

  const runReport = async () => {
    if (!xerFile) return;
    try {
      setLoading(true);
      setError(null);
      const res = await overallReport(xerFile, images);
      setResponse(res);
      await api.post('/ai-analysis', {
        name: `Overall Report - ${xerFile.name}`,
        analysisType: 'performance_analysis',
        description: 'Overall system report from AI backend',
        inputParameters: { fileName: xerFile.name, imageNames: images.map((i) => i.name) },
        algorithms: { primary: 'custom' },
        dataSources: ['external_ai_backend'],
        executionSettings: { initiatedFrom: 'frontend', externalBaseUrl: AI_BASE_URL },
        results: res.data,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Overall Report</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Upload .xer file</Typography>
          <input type="file" accept=".xer" onChange={handleXerChange} />
          {xerFile && (
            <Box sx={{ mt: 2 }}>
              <Chip label={xerFile.name} variant="outlined" />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Optional: Upload images</Typography>
            <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
            {images.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {images.map((img) => (
                  <Chip key={img.name} label={img.name} variant="outlined" />
                ))}
              </Box>
            )}
          </Box>
          <Button variant="contained" startIcon={<UploadIcon />} disabled={loading} onClick={runReport}>
            Run Report
          </Button>
          {response && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2">Response Status: {response.status}</Typography>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(response.data, null, 2)}</pre>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OverallReportPage;